import { z } from 'zod';
import { GRID_SIZES, LANGUAGES, LIMITS, PAYMENT_STATUSES, PLANS } from './constants';

// Default validation messages in Portuguese (schemas below set specific ones where it matters).
z.setErrorMap((issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      return { message: issue.received === 'undefined' ? 'Campo obrigatório ausente' : 'Tipo de dado inválido' };
    case z.ZodIssueCode.invalid_enum_value:
    case z.ZodIssueCode.invalid_literal:
    case z.ZodIssueCode.invalid_union:
      return { message: 'Valor inválido' };
    case z.ZodIssueCode.too_small:
      return { message: issue.type === 'array' ? 'Itens insuficientes' : 'Valor muito curto ou pequeno' };
    case z.ZodIssueCode.too_big:
      return { message: issue.type === 'array' ? 'Itens demais' : 'Valor muito longo ou grande' };
    case z.ZodIssueCode.invalid_string:
      return { message: 'Formato inválido' };
    default:
      return { message: ctx.defaultError };
  }
});

const id = z.string().trim().min(1).max(64);
const icon = z.string().min(1).max(16);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Cor inválida');
const label = z.string().trim().min(1).max(LIMITS.maxLabelLength);

/** An image embedded as a data: URL (only JPEG, PNG or WebP). */
export const imageDataUrlSchema = z
  .string()
  .max(LIMITS.maxImageDataUrlLength, 'Imagem muito grande')
  .regex(/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/, 'Formato de imagem inválido');

export const customCategorySchema = z.object({
  id,
  name: z.string().trim().min(1).max(LIMITS.maxCategoryNameLength),
  icon,
  color: hexColor,
});
export type CustomCategory = z.infer<typeof customCategorySchema>;

export const customSymbolSchema = z.object({
  id,
  categoryId: id,
  /**
   * Category name, kept up to date only so the previous version of the app
   * (which referenced categories by name) can still read the data.
   */
  category: z.string().max(LIMITS.maxCategoryNameLength).optional(),
  text: z.object({ pt: label, en: label }),
  icon,
  image: imageDataUrlSchema.nullable().optional(),
});
export type CustomSymbol = z.infer<typeof customSymbolSchema>;

function uniqueIds<T extends { id: string }>(items: T[]) {
  return new Set(items.map((i) => i.id)).size === items.length;
}

export const categoriesPayloadSchema = z
  .array(customCategorySchema)
  .max(LIMITS.maxCategories, `Máximo de ${LIMITS.maxCategories} categorias`)
  .refine(uniqueIds, 'Categorias com identificador repetido');

export const symbolsPayloadSchema = z
  .array(customSymbolSchema)
  .max(LIMITS.maxSymbols, `Máximo de ${LIMITS.maxSymbols} símbolos`)
  .refine(uniqueIds, 'Símbolos com identificador repetido');

/**
 * How often each symbol was pressed, for the "Frequentes" screen:
 * symbol id → { c: weighted count, t: last press (ms since epoch) }.
 */
export const usageEntrySchema = z.object({ c: z.number().min(0).max(1_000_000), t: z.number().int().min(0) });
export const usagePayloadSchema = z
  .record(id, usageEntrySchema)
  .refine((u) => Object.keys(u).length <= LIMITS.maxUsageEntries, `Máximo de ${LIMITS.maxUsageEntries} registros de uso`);
export type Usage = z.infer<typeof usagePayloadSchema>;

/**
 * Symbols and categories the caregiver hid from the child ("Ocultar"), by id.
 * Hidden items are kept, just not shown, so they can be shown again later.
 */
export const hiddenPayloadSchema = z.object({
  symbols: z.array(id).max(LIMITS.maxHiddenSymbols),
  categories: z.array(id).max(LIMITS.maxHiddenCategories),
});
export type Hidden = z.infer<typeof hiddenPayloadSchema>;
export const EMPTY_HIDDEN: Hidden = { symbols: [], categories: [] };

export const ROUTINE_KINDS = ['sequence', 'firstThen'] as const;
export type RoutineKind = (typeof ROUTINE_KINDS)[number];

/**
 * A visual routine: a sequence of steps (e.g. wake up → brush teeth → breakfast)
 * or "Primeiro → Depois" (exactly two steps). Steps are symbol ids, so they use
 * the board's own pictures (including family photos).
 */
export const routineSchema = z
  .object({
    id,
    name: z.string().trim().min(1).max(LIMITS.maxRoutineNameLength),
    kind: z.enum(ROUTINE_KINDS),
    steps: z.array(id).min(1).max(LIMITS.maxRoutineSteps),
  })
  .refine((r) => r.kind !== 'firstThen' || r.steps.length === 2, 'Primeiro → Depois tem exatamente 2 passos');
export type Routine = z.infer<typeof routineSchema>;

export const routinesPayloadSchema = z
  .array(routineSchema)
  .max(LIMITS.maxRoutines, `Máximo de ${LIMITS.maxRoutines} rotinas`)
  .refine(uniqueIds, 'Rotinas com identificador repetido');

export const settingsSchema = z.object({
  voiceGender: z.enum(['female', 'male']),
  voiceSpeed: z.number().min(0.5).max(2),
  gridSize: z.union([z.literal(GRID_SIZES[0]), z.literal(GRID_SIZES[1]), z.literal(GRID_SIZES[2])]),
  language: z.enum(LANGUAGES),
  /** Preferred voice (SpeechSynthesisVoice.voiceURI) on the device that chose it. */
  voiceURI: z.string().max(300).nullable(),
  /** Speak each symbol as soon as it is pressed (the sentence is still spoken with "Falar"). */
  // Default for data saved by app versions that did not have this option yet.
  speakOnTap: z.boolean().default(true),
});
export type Settings = z.infer<typeof settingsSchema>;

export const DEFAULT_SETTINGS: Settings = {
  voiceGender: 'female',
  voiceSpeed: 1,
  gridSize: 6,
  language: 'pt',
  voiceURI: null,
  speakOnTap: true,
};

// ─── Auth & account ────────────────────────────────────────────────────────

const email = z.string().trim().toLowerCase().email('E-mail inválido').max(254);

export const registerSchema = z.object({
  name: z.string().trim().min(1, 'Informe seu nome').max(100),
  email,
  password: z
    .string()
    .min(LIMITS.minPasswordLength, `A senha deve ter pelo menos ${LIMITS.minPasswordLength} caracteres`)
    .max(LIMITS.maxPasswordLength),
  paymentMethodId: z.string().regex(/^pm_[A-Za-z0-9_]+$/).optional(),
});

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Informe a senha').max(LIMITS.maxPasswordLength),
});

export const pinSchema = z.string().regex(/^\d{4}$/, 'O PIN deve ter exatamente 4 dígitos');

export const setPinSchema = z.object({ pin: pinSchema, currentPin: pinSchema.optional() });
export const verifyPinSchema = z.object({ pin: pinSchema });

export const forgotPasswordSchema = z.object({ email });

export const resetPasswordSchema = z.object({
  token: z.string().min(20, 'Link inválido').max(100, 'Link inválido'),
  password: registerSchema.shape.password,
});

export const changePasswordSchema = z.object({
  /** Not needed for Google-only accounts, which have no password yet. */
  currentPassword: z.string().max(LIMITS.maxPasswordLength).optional(),
  newPassword: registerSchema.shape.password,
});

/** Word the person types to confirm deleting their own account. */
export const DELETE_ACCOUNT_WORD = 'EXCLUIR';

export const deleteAccountSchema = z.object({
  confirm: z.literal(DELETE_ACCOUNT_WORD, { errorMap: () => ({ message: `Digite ${DELETE_ACCOUNT_WORD} para confirmar` }) }),
  /** Required for accounts that have a password (not for Google-only accounts). */
  password: z.string().max(LIMITS.maxPasswordLength).optional(),
});

export const paymentMethodSchema = z.object({ paymentMethodId: z.string().regex(/^pm_[A-Za-z0-9_]+$/) });

/** Test account created by an administrator: full access, no card, no billing. */
export const adminCreateUserSchema = z.object({
  name: registerSchema.shape.name,
  email,
  password: registerSchema.shape.password,
});

export const adminPlanSchema = z.object({
  plan: z.enum(PLANS),
  paymentStatus: z.enum(PAYMENT_STATUSES).optional(),
});
